<script setup lang="ts">
import "@maersk-global/mds-components-core/mc-number-stepper";
import "@maersk-global/mds-components-core/mc-checkbox";
import { computed, ref, toRaw, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useFiltersStore } from "@/store";
import { FilterTypeEnum } from "@/features/filters";
import type { BucketRange } from "@/features/control-tower/interfaces/update-widget";
import { MAX_THRESHOLD_DAYS, MIN_THRESHOLD_DAYS } from "@/features/control-tower/util/widgetLocalFilters";

interface FilterDefinitionProp {
  key: string;
  label?: string;
  type: string;
  value?: BucketRange[];
  storeZoneSuffix?: string;
  [k: string]: any;
}

const props = defineProps<{
  filter: FilterDefinitionProp;
  filterType: string;
  zonePrefix: string;
}>();

const { t } = useI18n();
const filtersStore = useFiltersStore();

const MAX_DAYS = MAX_THRESHOLD_DAYS;
const MIN_DAYS = MIN_THRESHOLD_DAYS;

/**
 * Determines the store zone based on filter's storeZoneSuffix
 */
const storeZone = computed(() =>
  props.filter.storeZoneSuffix ? `${props.zonePrefix}${props.filter.storeZoneSuffix}` : props.zonePrefix
);

const selectedFilter = computed(() => {
  const source =
    props.filterType === FilterTypeEnum.PANEL_FILTERS ? filtersStore.panelSelectedFilters : filtersStore.filtersApplied;
  return source?.[storeZone.value]?.[props.filter.key];
});

const getInitialRanges = (): BucketRange[] => {
  // First priority: user's saved data from the store
  const storeRanges = selectedFilter.value?.value;
  if (Array.isArray(storeRanges) && storeRanges.length > 0) {
    return storeRanges.map((r: any) => ({ ...r }));
  }
  // Second priority: widget catalog defaults
  if (Array.isArray(props.filter.value) && props.filter.value.length > 0) {
    return props.filter.value.map((r) => ({ ...r }));
  }
  // Fallback
  return [
    { min: 0, max: 7, enabled: true },
    { min: 8, max: 14, enabled: true },
    { min: 15, max: MAX_DAYS, enabled: true }
  ];
};

/**
 * Local ranges state, initialised from store or filter.value
 */
const ranges = ref<BucketRange[]>(getInitialRanges());

/**
 * At least one range must be enabled
 */
const enabledCount = computed(() => ranges.value.filter((r) => r.enabled).length);

/**
 * Stores a validation message to show the user why a value was clamped
 */
const validationMessage = ref<{ index: number; text: string } | null>(null);

/**
 * Tracks which stepper inputs are currently completely empty/NaN
 */
const invalidFields = ref<Set<string>>(new Set());

/**
 * Computes whether the entire filter has valid data
 */
const isValid = computed(() => invalidFields.value.size === 0);

// Track internal updates to prevent infinite loops when syncing to store
let isInternalSync = false;

/**
 * Writes the current ranges array into the filter store
 */
const syncToStore = () => {
  isInternalSync = true;
  filtersStore.applyPanelSelectedFilters(storeZone.value, props.filter.key, {
    filterKey: props.filter.key,
    label: props.filter.label,
    value: structuredClone(toRaw(ranges.value)),
    filterType: props.filterType as FilterTypeEnum,
    filterLabel: props.filter.label,
    isValid: isValid.value
  });
  // Next cycle will pick up the change; we could also use nextTick or just reset
  isInternalSync = false;
};

/**
 * Compute the allowed minimum for a stepper
 * - For a "max" field: must be >= this range's min, and >= previous range's max
 * - For a "min" field: must be >= previous range's min, else MIN_DAYS
 */
const getAllowedMin = (rangeIndex: number, field: "min" | "max"): number => {
  if (field === "max") {
    const prevMax = rangeIndex > 0 ? ranges.value[rangeIndex - 1].max : MIN_DAYS;
    return Math.max(MIN_DAYS, ranges.value[rangeIndex].min, prevMax);
  }
  // field === "min" — must be >= previous range's min, but never below MIN_DAYS (0)
  if (rangeIndex > 0) {
    return Math.max(MIN_DAYS, ranges.value[rangeIndex - 1].min);
  }
  return MIN_DAYS;
};

/**
 * Compute the allowed maximum for a stepper
 * - For a "min" field: must be <= this range's max, and <= next range's min
 * - For a "max" field: must be <= next range's max, else MAX_DAYS
 */
const getAllowedMax = (rangeIndex: number, field: "min" | "max"): number => {
  if (field === "min") {
    const nextMin = rangeIndex < ranges.value.length - 1 ? ranges.value[rangeIndex + 1].min : MAX_DAYS;
    return Math.min(ranges.value[rangeIndex].max, nextMin);
  }
  // field === "max" — must be <= next range's max
  if (rangeIndex < ranges.value.length - 1) {
    return ranges.value[rangeIndex + 1].max;
  }
  return MAX_DAYS;
};

/**
 * Force a mc-number-stepper web component to display a specific value.
 * The web component (Lit-based) uses:
 *   - `.value` property (Lit reactive property)
 *   - `.inputElement.value` (the inner <input> display value)
 *   - `value` attribute (HTML attribute, used for initial render)
 * We set all three to guarantee the displayed value is corrected.
 */
const forceStepperValue = (stepperEl: any, val: number) => {
  const strVal = String(val);
  try {
    // 1. Set the Lit property
    stepperEl.value = strVal;
    // 2. Set the HTML attribute
    stepperEl.setAttribute("value", strVal);
    // 3. Set the internal input element's display value
    if (stepperEl.inputElement) {
      stepperEl.inputElement.value = strVal;
    }
    // 4. Also schedule a delayed sync in case Lit overwrites during its update cycle
    requestAnimationFrame(() => {
      try {
        stepperEl.value = strVal;
        if (stepperEl.inputElement) {
          stepperEl.inputElement.value = strVal;
        }
      } catch {
        /* test env */
      }
    });
  } catch {
    // Tests use mock events — safe to ignore
  }
};

const getBelowBoundsMessage = (
  field: "min" | "max",
  lowerBound: number,
  rangesValue: BucketRange[],
  rangeIndex: number
) => {
  if (field === "min") {
    return {
      index: rangeIndex,
      text:
        lowerBound === MIN_DAYS
          ? t("CONTROL_TOWER.WIDGET_FILTERS.CANNOT_BE_LESS_THAN_ZERO")
          : t("CONTROL_TOWER.WIDGET_FILTERS.MIN_CANNOT_BE_LESS_THAN_PREV_MIN")
    };
  }
  return {
    index: rangeIndex,
    text:
      lowerBound === rangesValue[rangeIndex].min
        ? t("CONTROL_TOWER.WIDGET_FILTERS.MAX_CANNOT_BE_LESS_THAN_MIN")
        : t("CONTROL_TOWER.WIDGET_FILTERS.MAX_CANNOT_BE_LESS_THAN_PREV_MAX")
  };
};

const getAboveBoundsMessage = (
  field: "min" | "max",
  upperBound: number,
  rangesValue: BucketRange[],
  rangeIndex: number
) => {
  if (field === "max") {
    return {
      index: rangeIndex,
      text:
        upperBound === MAX_DAYS
          ? t("CONTROL_TOWER.WIDGET_FILTERS.CANNOT_EXCEED_MAX")
          : t("CONTROL_TOWER.WIDGET_FILTERS.MAX_CANNOT_EXCEED_NEXT_MAX")
    };
  }
  return {
    index: rangeIndex,
    text:
      upperBound === rangesValue[rangeIndex].max
        ? t("CONTROL_TOWER.WIDGET_FILTERS.MIN_CANNOT_EXCEED_MAX")
        : t("CONTROL_TOWER.WIDGET_FILTERS.MIN_CANNOT_EXCEED_NEXT_MIN")
  };
};

/**
 * Returns the appropriate validation message when a stepper value exceeds bounds
 */
const getValidationMessage = (
  field: "min" | "max",
  raw: number,
  lowerBound: number,
  upperBound: number,
  rangeIndex: number,
  rangesValue: BucketRange[]
): { index: number; text: string } | null => {
  if (raw < lowerBound) return getBelowBoundsMessage(field, lowerBound, rangesValue, rangeIndex);
  if (raw > upperBound) return getAboveBoundsMessage(field, upperBound, rangesValue, rangeIndex);
  return null;
};

/**
 * Helper to handle empty, null, and NaN inputs. Returns true if the input was invalid.
 */
const handleInvalidInput = (rawString: string, rangeIndex: number, fieldKey: string, field: "min" | "max"): boolean => {
  if (rawString === "" || rawString === null || rawString === undefined) {
    invalidFields.value.add(fieldKey);
    ranges.value[rangeIndex][field] = null as any;

    if (validationMessage.value?.index === rangeIndex) {
      validationMessage.value = null;
    }

    syncToStore();
    return true;
  }

  const raw = Number(rawString);
  if (Number.isNaN(raw)) {
    invalidFields.value.add(fieldKey);
    syncToStore();
    return true;
  }

  return false;
};

/**
 * Handler for min/max stepper changes — clamps to maintain monotonic ordering
 */
const handleStepperChange = (rangeIndex: number, field: "min" | "max", event: Event) => {
  const stepperEl = event.target as any;
  const rawString = stepperEl.value;
  const fieldKey = `${rangeIndex}-${field}`;

  if (handleInvalidInput(rawString, rangeIndex, fieldKey, field)) {
    return;
  }

  const raw = Number(rawString);

  // If we got here, it's a valid number
  if (invalidFields.value.has(fieldKey)) {
    invalidFields.value.delete(fieldKey);
  }

  const lowerBound = getAllowedMin(rangeIndex, field);
  const upperBound = getAllowedMax(rangeIndex, field);
  const clamped = Math.max(MIN_DAYS, Math.max(lowerBound, Math.min(upperBound, raw)));

  // If the user tried to go outside the bounds, show a helpful message
  const newMessage = getValidationMessage(field, raw, lowerBound, upperBound, rangeIndex, ranges.value);
  if (newMessage) {
    validationMessage.value = newMessage;
  } else if (validationMessage.value?.index === rangeIndex) {
    // Clear message if they are within bounds
    validationMessage.value = null;
  }

  // Always force the stepper when the value was corrected.
  // If clamped === previous reactive value, Vue won't re-render :value,
  // so we must imperatively reset the web component's display.
  if (clamped !== raw) {
    forceStepperValue(stepperEl, clamped);
  }

  ranges.value[rangeIndex][field] = clamped;
  syncToStore();
};

/**
 * Handler for enable/disable toggle
 */
const handleToggle = (rangeIndex: number) => {
  // Prevent disabling the last enabled range
  if (ranges.value[rangeIndex].enabled && enabledCount.value <= 1) {
    return;
  }
  ranges.value[rangeIndex].enabled = !ranges.value[rangeIndex].enabled;
  syncToStore();
};

// Seed the store on mount
syncToStore();

// Watch for external store changes (e.g., when FilterPanel parses saved data and seeds store)
watch(
  () => selectedFilter.value,
  (newVal) => {
    if (isInternalSync) return;
    const storeRanges = newVal?.value;
    if (Array.isArray(storeRanges) && storeRanges.length > 0) {
      // Ensure we don't trigger a circular sync when just applying external data
      isInternalSync = true;
      ranges.value = storeRanges.map((r: any) => ({ ...r }));
      isInternalSync = false;
    }
  },
  { deep: true, immediate: true }
);

// Reseed if the filter catalog value changes externally as backup
watch(
  () => props.filter.value,
  (newVal) => {
    if (isInternalSync) return;
    if (!selectedFilter.value?.value && Array.isArray(newVal) && newVal.length > 0) {
      isInternalSync = true;
      ranges.value = newVal.map((r) => ({ ...r }));
      syncToStore();
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="bucket-range-filter" :data-test="`bucket-range-filter/${filter.key}`">
    <div
      v-for="(range, index) in ranges"
      :key="index"
      class="bucket-range-filter__item"
      :data-test="`bucket-range-filter/${filter.key}/range-${index}`"
    >
      <div class="bucket-range-filter__content" :class="{ 'bucket-range-filter__content--disabled': !range.enabled }">
        <p class="bucket-range-filter__label mds-text--small-bold">
          {{ t("CONTROL_TOWER.WIDGET_FILTERS.RANGE") }} {{ index + 1 }}
        </p>

        <!-- Min / Max row -->
        <div class="bucket-range-filter__steppers">
          <div class="bucket-range-filter__stepper-group">
            <label
              class="bucket-range-filter__stepper-label mds-text--x-small"
              :for="`${filter.key}-range-${index}-min`"
            >
              {{ t("CONTROL_TOWER.WIDGET_FILTERS.MIN") }}
            </label>
            <mc-number-stepper
              :id="`${filter.key}-range-${index}-min`"
              :value="range.min"
              :min="MIN_DAYS"
              :max="MAX_DAYS"
              :step="1"
              suffix="days"
              hiddenlabel="true"
              fit="small"
              :disabled="!range.enabled"
              :class="{ 'bucket-range-filter__stepper--error': invalidFields.has(`${index}-min`) }"
              :data-test="`bucket-range-filter/${filter.key}/range-${index}/min`"
              @input="(e: Event) => handleStepperChange(index, 'min', e)"
            />
            <p v-if="invalidFields.has(`${index}-min`)" class="bucket-range-filter__error mds-text--x-small">
              {{ t("CONTROL_TOWER.WIDGET_FILTERS.MUST_BE_A_NUMBER") }}
            </p>
          </div>

          <div class="bucket-range-filter__stepper-group">
            <label
              class="bucket-range-filter__stepper-label mds-text--x-small"
              :for="`${filter.key}-range-${index}-max`"
            >
              {{ t("CONTROL_TOWER.WIDGET_FILTERS.MAX") }}
            </label>
            <mc-number-stepper
              :id="`${filter.key}-range-${index}-max`"
              :value="range.max"
              :min="MIN_DAYS"
              :max="MAX_DAYS"
              :step="1"
              suffix="days"
              hiddenlabel="true"
              fit="small"
              :disabled="!range.enabled"
              :class="{ 'bucket-range-filter__stepper--error': invalidFields.has(`${index}-max`) }"
              :data-test="`bucket-range-filter/${filter.key}/range-${index}/max`"
              @input="(e: Event) => handleStepperChange(index, 'max', e)"
            />
            <p v-if="invalidFields.has(`${index}-max`)" class="bucket-range-filter__error mds-text--x-small">
              {{ t("CONTROL_TOWER.WIDGET_FILTERS.MUST_BE_A_NUMBER") }}
            </p>
          </div>
        </div>

        <!-- Validation Error Message -->
        <p
          v-if="validationMessage && validationMessage.index === index"
          class="bucket-range-filter__error mds-text--x-small"
        >
          {{ validationMessage.text }}
        </p>
      </div>

      <!-- Enable toggle -->
      <mc-checkbox
        :checked="range.enabled"
        :disabled="range.enabled && enabledCount <= 1"
        fit="small"
        :label="t('CONTROL_TOWER.WIDGET_FILTERS.ENABLE')"
        :data-test="`bucket-range-filter/${filter.key}/range-${index}/toggle`"
        @change="() => handleToggle(index)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.bucket-range-filter {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity 0.2s ease;

    &--disabled {
      opacity: 0.5;
    }
  }

  &__label {
    margin: 0;
    color: var(--mds_brand_appearance_neutral_default_text-color);
  }

  &__steppers {
    display: flex;
    gap: 12px;
  }

  &__stepper-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    mc-number-stepper {
      width: 100%;
    }
  }

  &__stepper-label {
    color: var(--mds_brand_appearance_neutral_weak_text-color);
  }

  &__stepper--error::part(input) {
    border-color: var(--mds_brand_appearance_danger_default_text-color, #d32f2f);
  }

  &__error {
    margin: 0;
    color: var(--mds_brand_appearance_danger_default_text-color, #d32f2f);
    margin-top: -8px;
  }
}
</style>
